// @generated-imports
import { continueLastGame, findGameById, openGameModal, renderNextCatalogPage, showToast } from './app.js';
import { dom, state } from './core.js';
import { anyLayerOpen, layerConfirm, layerMove, topLayer } from './layers.js';
import { FOCUSABLE_SELECTOR, smoothBehavior } from './lite.js';
import { cycleSelect, updatePadLegend } from './platform.js';
// @generated-imports
/* ===========================================================================
   NAVEGAÇÃO DO PORTAL PELO CONTROLE (modo sofá)
   ---------------------------------------------------------------------------
   D-pad/analógico percorre os cards, A joga, B fecha modal/seletor,
   Start continua o último jogo. Só age quando a PÁGINA DO PORTAL tem o
   foco — com um jogo rodando, o foco está no iframe e o Chromium nem
   entrega dados de gamepad aqui, então não há conflito de inputs.
   ======================================================================== */
const GP_BUTTON = {
  A: 0, B: 1, X: 2, Y: 3,
  LB: 4, RB: 5, LT: 6, RT: 7,
  SELECT: 8, START: 9,
  UP: 12, DOWN: 13, LEFT: 14, RIGHT: 15,
};

/** Layout Nintendo (8BitDo em modo Switch, pads Nintendo, Joy-Con): o botão
 *  FÍSICO "A" reporta índice 1 no standard mapping (que segue o layout
 *  Xbox). Trocamos o par confirmar/cancelar para casar com o que está
 *  escrito no plástico do controle — sem isto, "A" voltava e "B" confirmava. */
function gpConfirmCancel(pad) {
  // "pro controller" e o vendor 057e cobrem o controle OFICIAL da Nintendo,
  // cujo id não contém "nintendo" nem "switch" (ex.: "Pro Controller
  // (STANDARD GAMEPAD Vendor: 057e Product: 2009)"). Limitação conhecida:
  // 8BitDo em modo XInput se identifica como "Xbox 360 Controller" e é
  // indistinguível pelo id — nesse modo vale o layout Xbox (ver README).
  const nintendoLayout = /8bitdo|nintendo|switch|joy-?con|pro controller|057e/i.test(String(pad.id || ''));
  return nintendoLayout
    ? { confirm: 1, cancel: 0 }
    : { confirm: GP_BUTTON.A, cancel: GP_BUTTON.B };
}

/** D-pad exposto como "hat" em axes[9] — padrão comum quando o navegador
 *  não reconhece o pad (mapping !== 'standard'; típico de WebView de TV e
 *  Linux). São 8 posições de -1 (cima) a +1 (cima-esquerda) em passos de
 *  2/7; em repouso o eixo fica FORA de [-1, 1] (≈ 1.2857). Diagonais são
 *  achatadas para a direção cardinal mais útil na grade. */
const GP_HAT_DIRECTIONS = ['up', 'right', 'right', 'down', 'down', 'left', 'left', 'up'];

function gpHatDirection(pad) {
  if (pad.mapping === 'standard' || !pad.axes || pad.axes.length <= 9) return null;
  const value = pad.axes[9];
  if (typeof value !== 'number' || value < -1.01 || value > 1.01) return null;
  const index = Math.round((value + 1) / (2 / 7));
  return GP_HAT_DIRECTIONS[Math.max(0, Math.min(7, index))] || null;
}

/** "8BitDo Ultimate 2.4G (Vendor: 2dc8 Product: 3106)" → "8BitDo Ultimate 2.4G" */
function gpShortName(id) {
  return String(id || 'Controle').replace(/\s*\(.*$/, '').trim().slice(0, 40) || 'Controle';
}

/** Chip permanente de status do controle no header do player — responde à
 *  dúvida "o controle caiu ou o jogo travou?" sem depender de toast. */
function setGamepadStatus(mode, name) {
  if (!dom.gamepadStatus) return;
  if (name) state.lastGamepadName = name;
  const label = state.lastGamepadName || 'Controle';
  dom.gamepadStatus.classList.remove('is-hidden', 'is-on', 'is-off', 'is-stalled');
  dom.gamepadStatus.classList.add(mode === 'on' ? 'is-on' : mode === 'stalled' ? 'is-stalled' : 'is-off');
  dom.gamepadStatusText.textContent = mode === 'on'
    ? `${label} conectado`
    : mode === 'stalled'
      ? `${label} sem eventos — clique/toque no jogo`
      : `${label} desconectado`;
}
const GP_MOVE_INTERVAL = 190;  // ms entre passos com direção segurada
const GP_AXIS_DEADZONE = 0.55;

function setupGamepadNavigation() {
  if (!navigator.getGamepads) return;

  /** Algum botão/eixo do pad está ativo agora? */
  function padHasInput(pad) {
    if (!pad) return false;
    try {
      for (let i = 0; i < pad.buttons.length; i += 1) {
        const button = pad.buttons[i];
        if (button && (button.pressed || button.value > 0.5)) return true;
      }
      if (Math.abs(pad.axes[0] || 0) > GP_AXIS_DEADZONE) return true;
      if (Math.abs(pad.axes[1] || 0) > GP_AXIS_DEADZONE) return true;
      if (gpHatDirection(pad)) return true;
    } catch (_) {}
    return false;
  }

  /** Multi-pad: quem tem atividade AGORA assume a navegação; sem atividade,
   *  fica com o último pad usado ("sticky"); na falta, o primeiro. Antes só
   *  o 1º pad navegava o catálogo — o jogador 2 ficava de fora. */
  function pickNavPad() {
    let pads;
    try { pads = navigator.getGamepads(); } catch (_) { return null; }
    let first = null;
    let sticky = null;
    for (let i = 0; i < pads.length; i += 1) {
      const pad = pads[i];
      if (!pad) continue;
      if (!first) first = pad;
      if (pad.index === state.gpNavPadIndex) sticky = pad;
      if (padHasInput(pad)) return pad;
    }
    return sticky || first;
  }

  function tick() {
    window.requestAnimationFrame(tick);
    // Guarda barata ANTES de getGamepads(): durante o jogo, nem o custo do
    // snapshot de gamepads (que copia estado de todos os pads) é pago pela
    // página pai — cada microssegundo do rAF pertence ao emulador.
    // Exceção: com uma camada do portal aberta durante o jogo (menu de pausa,
    // estados, jogadores…), o controle navega a camada.
    if (document.visibilityState === 'hidden'
      || ((state.currentIframe || dom.emulatorMount.classList.contains('is-active')) && !anyLayerOpen())) {
      state.gpNavPrimed = false;
      return;
    }
    const pad = pickNavPad();
    if (!pad) { state.gpNavPrimed = false; return; }
    updatePadLegend();
    if (pad.index !== state.gpNavPadIndex) {
      // Troca de pad: novo priming — o 1º toque do pad que assume só toma
      // o comando (sem ação fantasma); do 2º em diante, age normalmente.
      state.gpNavPadIndex = pad.index;
      state.gpNavPrimed = false;
    }
    handleGamepadInput(pad);
  }

  window.requestAnimationFrame(tick);

  // Chip de status: eventos do próprio portal (fora do jogo, o iframe não
  // existe e é a página pai quem enxerga conectar/desconectar).
  window.addEventListener('gamepadconnected', (event) => {
    setGamepadStatus('on', gpShortName(event.gamepad && event.gamepad.id));
    updatePadLegend();
  });
  window.addEventListener('gamepaddisconnected', () => {
    const pads = navigator.getGamepads ? [...navigator.getGamepads()].filter(Boolean) : [];
    if (pads.length) setGamepadStatus('on', gpShortName(pads[0].id));
    else setGamepadStatus('off', '');
    updatePadLegend();
  });
  updatePadLegend();

  // Mouse retoma o comando: some o realce do controle
  document.addEventListener('pointerdown', clearGpFocus, true);
}

function gpPressed(pad, index) {
  const button = pad.buttons[index];
  return Boolean(button && (button.pressed || button.value > 0.5));
}

/**
 * REGRA CENTRAL: com um jogo carregado, o gamepad pertence AO JOGO.
 * O Chromium entrega os dados do controle à aba inteira (portal E
 * iframe recebem os mesmos botões ao mesmo tempo), então a navegação
 * do catálogo precisa se auto-desligar durante a sessão — senão o "A"
 * dentro do jogo/menu de controles vira "abrir outro jogo" no portal.
 */
function isGamepadNavAllowed() {
  // Uma camada do portal aberta (menu de pausa, estados, jogadores…) recebe
  // o controle mesmo com jogo em andamento — o foco está nela, não no iframe.
  if (anyLayerOpen()) return document.visibilityState !== 'hidden';
  // Sessão de jogo ativa (catálogo ou ROM local): o gamepad pertence ao
  // jogo — navegação do portal suspensa até o Desligar.
  if (state.currentIframe) return false;
  if (dom.emulatorMount.classList.contains('is-active')) return false;
  // Iframe focado (defesa extra): o input é dele.
  const active = document.activeElement;
  if (active && active.tagName === 'IFRAME') return false;
  // Aba realmente em segundo plano: não navega. Usamos visibilidade
  // (confiável) em vez de document.hasFocus() — este último retorna
  // false em cenário de TV/gamepad, onde a janela está visível e à
  // frente mas nenhum elemento "focável" recebeu clique, e bloquearia
  // a navegação indevidamente logo após sair de um jogo.
  if (document.visibilityState === 'hidden') return false;
  return true;
}

function handleGamepadInput(pad) {
  if (!isGamepadNavAllowed()) {
    state.gpNavPrimed = false;
    return;
  }

  // Priming: no 1º tick após reabilitar, registra o que JÁ está
  // pressionado sem agir — o botão segurado ao sair do jogo não pode
  // virar comando fantasma no catálogo.
  if (!state.gpNavPrimed) {
    state.gpNavPrimed = true;
    [GP_BUTTON.A, GP_BUTTON.B, GP_BUTTON.X, GP_BUTTON.Y, GP_BUTTON.START,
      GP_BUTTON.LB, GP_BUTTON.RB, GP_BUTTON.LT, GP_BUTTON.RT,
      GP_BUTTON.UP, GP_BUTTON.DOWN, GP_BUTTON.LEFT, GP_BUTTON.RIGHT]
      .forEach((index) => { state.gpNavPrevPressed[index] = gpPressed(pad, index); });
    state.gpNavLastMove = Date.now();
    // Pad sem standard mapping: avisa (uma vez) que o d-pad funciona via
    // modo de compatibilidade — antes ele simplesmente não respondia.
    if (pad.mapping !== 'standard' && !state.gpCompatToastShown) {
      state.gpCompatToastShown = true;
      showToast('🎮 Controle sem mapeamento padrão — modo de compatibilidade ativado (d-pad via eixo).');
    }
    return;
  }

  function justPressed(index) {
    const now = gpPressed(pad, index);
    const was = Boolean(state.gpNavPrevPressed[index]);
    state.gpNavPrevPressed[index] = now;
    return now && !was;
  }

  // Layout do pad decide quem confirma e quem cancela (Xbox vs Nintendo).
  const { confirm: confirmBtn, cancel: cancelBtn } = gpConfirmCancel(pad);

  // Qualquer camada aberta (detalhes, backup, ajuda, configurações, seletor
  // de core, confirmação, estados, jogadores, fila, edição) é tratada pelo
  // gerenciador de camadas — nada vaza para o catálogo atrás dela.
  const layer = topLayer();
  if (layer && layer.passive) {
    // Camada que lê o controle por conta própria (teste do controle): o
    // portal não navega nem fecha — ela decide.
    [confirmBtn, cancelBtn].forEach((index) => justPressed(index));
    return;
  }
  if (layer) {
    if (justPressed(cancelBtn)) { layer.close(); return; }
    if (justPressed(confirmBtn)) { layerConfirm(); return; }
    let layerDirection = null;
    if (gpPressed(pad, GP_BUTTON.LEFT) || pad.axes[0] < -GP_AXIS_DEADZONE) layerDirection = 'left';
    else if (gpPressed(pad, GP_BUTTON.RIGHT) || pad.axes[0] > GP_AXIS_DEADZONE) layerDirection = 'right';
    else if (gpPressed(pad, GP_BUTTON.UP) || pad.axes[1] < -GP_AXIS_DEADZONE) layerDirection = 'up';
    else if (gpPressed(pad, GP_BUTTON.DOWN) || pad.axes[1] > GP_AXIS_DEADZONE) layerDirection = 'down';
    if (!layerDirection) layerDirection = gpHatDirection(pad);
    if (!layerDirection) { state.gpNavLastMove = 0; return; }
    const nowLayer = Date.now();
    if (nowLayer - state.gpNavLastMove < GP_MOVE_INTERVAL) return;
    state.gpNavLastMove = nowLayer;
    layerMove(layerDirection);
    return;
  }

  // Confirmar
  if (justPressed(confirmBtn)) {
    const card = currentGpCard();
    if (card) {
      const play = card.querySelector('[data-play-id]');
      if (play) play.click();
    } else {
      moveGpFocus('right'); // primeiro toque só posiciona o realce
    }
    return;
  }

  // X: detalhes · Y: favoritar · LB/RB: sistema · LT/RT: gênero/ordenação.
  // Antes, a barra de filtros era inalcançável pelo controle — na TV o
  // catálogo era "só rolar".
  {
    const card = currentGpCard();
    if (justPressed(GP_BUTTON.X) && card) {
      const more = card.querySelector('[data-more-id]');
      if (more) { openGameModal(findGameById(more.dataset.moreId)); return; }
    }
    if (justPressed(GP_BUTTON.Y) && card) {
      const fav = card.querySelector('[data-fav-id]');
      if (fav) { fav.click(); return; }
    }
    if (justPressed(GP_BUTTON.LB)) { cycleSelect(dom.systemFilter, -1); return; }
    if (justPressed(GP_BUTTON.RB)) { cycleSelect(dom.systemFilter, 1); return; }
    if (justPressed(GP_BUTTON.LT)) { cycleSelect(dom.genreFilter, 1); return; }
    if (justPressed(GP_BUTTON.RT)) { cycleSelect(dom.sortSelect, 1); return; }
  }

  // Start: continuar último jogo
  if (justPressed(GP_BUTTON.START) && !dom.continueBtn.classList.contains('is-hidden')) {
    continueLastGame();
    return;
  }

  // Direções (com repetição controlada)
  let direction = null;
  if (gpPressed(pad, GP_BUTTON.LEFT) || pad.axes[0] < -GP_AXIS_DEADZONE) direction = 'left';
  else if (gpPressed(pad, GP_BUTTON.RIGHT) || pad.axes[0] > GP_AXIS_DEADZONE) direction = 'right';
  else if (gpPressed(pad, GP_BUTTON.UP) || pad.axes[1] < -GP_AXIS_DEADZONE) direction = 'up';
  else if (gpPressed(pad, GP_BUTTON.DOWN) || pad.axes[1] > GP_AXIS_DEADZONE) direction = 'down';
  // Fallback: d-pad como hat em axes[9] (pads sem standard mapping).
  if (!direction) direction = gpHatDirection(pad);

  if (!direction) { state.gpNavLastMove = 0; return; }
  const now = Date.now();
  if (now - state.gpNavLastMove < GP_MOVE_INTERVAL) return;
  state.gpNavLastMove = now;
  moveGpFocus(direction);
}

/** Cima/baixo circulam o foco entre os controles do painel de saves —
 *  junto com "confirmar aciona o focado" e "cancelar fecha", o painel
 *  inteiro fica operável sem mouse/teclado. */
function gpBackupMove(direction) {
  const card = dom.backupModal.querySelector('.backup-card');
  if (!card) return;
  if (direction === 'left' || direction === 'right') {
    // Esquerda/direita pulam de SEÇÃO (ações → dados → configs → ROMs →
    // zona de risco); com as listas maiores, andar item a item era longo.
    const sections = [
      card.querySelector('.backup-actions'),
      dom.emuDataList,
      dom.configList,
      dom.localRomsList,
      card.querySelector('.backup-danger'),
    ].filter(Boolean);
    if (!sections.length) return;
    const current = sections.findIndex((section) => section.contains(document.activeElement));
    for (let step = 1; step <= sections.length; step += 1) {
      const index = ((current < 0 ? -1 : current) + (direction === 'right' ? step : -step) + sections.length * 2) % sections.length;
      const target = [...sections[index].querySelectorAll(FOCUSABLE_SELECTOR)]
        .find((el) => !el.disabled && el.offsetParent !== null);
      if (target) {
        try { target.focus(); } catch (_) {}
        return;
      }
    }
    return;
  }
  const focusable = [...card.querySelectorAll(FOCUSABLE_SELECTOR)]
    .filter((el) => !el.disabled && el.offsetParent !== null);
  if (!focusable.length) return;
  let index = focusable.indexOf(document.activeElement);
  index += direction === 'down' ? 1 : -1;
  if (index < 0) index = focusable.length - 1;
  if (index >= focusable.length) index = 0;
  try { focusable[index].focus(); } catch (_) {}
}

/* --- Navegação por controle DENTRO do seletor de core --- */

function gpPickerOptions() {
  return [...dom.corePickerGrid.querySelectorAll('button')];
}

function gpPickerHighlight(index) {
  const options = gpPickerOptions();
  if (!options.length) return;
  const clamped = Math.max(0, Math.min(options.length - 1, index));
  options.forEach((option, i) => option.classList.toggle('gp-focus', i === clamped));
  state.gpPickerIndex = clamped;
  const option = options[clamped];
  try { option.focus(); } catch (_) {}
  try { option.scrollIntoView({ block: 'nearest' }); } catch (_) {}
}

function gpPickerMove(direction) {
  const options = gpPickerOptions();
  if (!options.length) return;
  if (state.gpPickerIndex < 0) { gpPickerHighlight(0); return; }
  const columns = gpColumns(options);
  let index = state.gpPickerIndex;
  if (direction === 'left') index -= 1;
  if (direction === 'right') index += 1;
  if (direction === 'up') index -= columns;
  if (direction === 'down') index += columns;
  gpPickerHighlight(index);
}

function gpCards() {
  return [...dom.catalogGrid.querySelectorAll('.game-card')];
}

function currentGpCard() {
  const cards = gpCards();
  return state.gpNavIndex >= 0 && state.gpNavIndex < cards.length
    ? cards[state.gpNavIndex] : null;
}

function gpColumns(cards) {
  if (!cards.length) return 1;
  const firstTop = cards[0].offsetTop;
  let columns = 0;
  for (let i = 0; i < cards.length; i += 1) {
    if (cards[i].offsetTop === firstTop) columns += 1;
    else break;
  }
  return Math.max(columns, 1);
}

function moveGpFocus(direction) {
  const cards = gpCards();
  if (!cards.length) return;

  let index = state.gpNavIndex;
  if (index < 0) {
    index = 0; // primeiro comando apenas acende o realce no card 1
  } else {
    const columns = gpColumns(cards);
    if (direction === 'left') index -= 1;
    if (direction === 'right') index += 1;
    if (direction === 'up') index -= columns;
    if (direction === 'down') index += columns;
    index = Math.max(0, Math.min(cards.length - 1, index));
  }

  if (index === state.gpNavIndex) {
    // borda inferior: tenta carregar o próximo lote
    if (direction === 'down') renderNextCatalogPage();
    return;
  }

  clearGpFocus();
  state.gpNavIndex = index;
  const card = cards[index];
  card.classList.add('gp-focus');
  try { card.scrollIntoView({ block: 'center', behavior: smoothBehavior() }); } catch (_) {
    try { card.scrollIntoView(); } catch (__) {}
  }
}

function clearGpFocus() {
  const previous = dom.catalogGrid.querySelector('.game-card.gp-focus');
  if (previous) previous.classList.remove('gp-focus');
  if (arguments.length) state.gpNavIndex = -1; // via pointerdown
}

/** Registro do service worker (PWA): só em https/localhost e quando suportado. */
function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  const isSecure = window.location.protocol === 'https:'
    || ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (!isSecure) return;

  const register = () => {
    navigator.serviceWorker.register('sw.js').then((registration) => {
      // Fluxo de atualização visível: sem isto, uma versão nova só valia a
      // partir do SEGUNDO reload e ninguém sabia por quê.
      registration.addEventListener('updatefound', () => {
        const worker = registration.installing;
        if (!worker) return;
        worker.addEventListener('statechange', () => {
          if (worker.state === 'installed' && navigator.serviceWorker.controller) {
            showToast('Nova versão do EmuTab instalada.', {
              action: { label: 'Recarregar agora', handler: () => window.location.reload() },
            });
          }
        });
      });
    }).catch(() => { /* PWA é opcional */ });
  };

  // O evento load pode já ter passado (script carregado tarde / cache):
  // nesse caso o listener antigo nunca disparava e o SW não registrava.
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register);
}

function announce(message) {
  dom.liveRegion.textContent = '';
  window.setTimeout(() => {
    dom.liveRegion.textContent = message;
  }, 40);
}

// @generated-exports
export { GP_AXIS_DEADZONE, GP_BUTTON, GP_HAT_DIRECTIONS, GP_MOVE_INTERVAL, announce, clearGpFocus, currentGpCard, gpBackupMove, gpCards, gpColumns, gpConfirmCancel, gpHatDirection, gpPickerHighlight, gpPickerMove, gpPickerOptions, gpPressed, gpShortName, handleGamepadInput, isGamepadNavAllowed, moveGpFocus, registerServiceWorker, setGamepadStatus, setupGamepadNavigation };
// @generated-exports
